// ABOUTME: One read filter of the object schema form: code, timeout, Cypher expression and description, plus its explain preview.
// ABOUTME: The preview counts the nodes the expression keeps for the caller and lists the Neo4j plan operators.

import {
    Alert,
    Box,
    Button,
    Chip,
    DeleteOutlinedIcon,
    IconButton,
    PlayArrowIcon,
    Stack,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import { InlineToggle } from '@src/_new/modules/product/components/ProductInfoViewer/FormComponent/components/InlineToggle';
import { TextFieldElement } from '@src/components/formFields';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useExplainReadFilter } from '../hooks/useExplainReadFilter';
import {
    parseTimeout,
    ReadFilterDraft,
    validateCode,
    validateExpression,
    validateTimeout,
} from '../utils/readFilterDrafts';
import { useReadFilterErrorMessages } from './useReadFilterErrorMessages';

interface ReadFilterRowProps {
    objectSchemaId: string;
    index: number;
    /** the tenant package shipping the filter, empty for a filter of the tenant */
    source: string;
    onRemove: () => void;
}

export function ReadFilterRow({
    objectSchemaId,
    index,
    source,
    onRemove,
}: ReadFilterRowProps): JSX.Element {
    const intl = useIntl();
    const errorMessage = useReadFilterErrorMessages();
    // Untyped like every form field element: the object schema form has no single values type.
    const { control, getValues } = useFormContext();
    const expression: string = useWatch({ control, name: `readFilters.${index}.expression` });
    const timeoutMs: string = useWatch({ control, name: `readFilters.${index}.timeoutMs` });
    const { loading, error, result, explain, reset } = useExplainReadFilter(objectSchemaId);

    // A preview only stands for the expression it was computed on.
    useEffect(() => {
        reset();
    }, [expression, timeoutMs, reset]);

    const isPackageFilter = Boolean(source);
    const toRuleResult = (code: ReturnType<typeof validateExpression>) =>
        code ? errorMessage(code) : true;
    const timeout = parseTimeout(timeoutMs);
    const canExplain = !validateExpression(expression) && !validateTimeout(timeoutMs);

    return (
        <Stack gap={2} data-testid="read-filter-row">
            {/* Same row pattern as the schema form: fields bottom-aligned, the side controls centred on the inputs. */}
            <Stack flexDirection="row" alignItems="flex-end" gap={1}>
                <TextFieldElement
                    required
                    control={control}
                    id={`readFilters.${index}.code`}
                    name={`readFilters.${index}.code`}
                    label={intl.formatMessage({ id: 'read-filters.code', defaultMessage: 'Code' })}
                    // Align recognises a package filter by its code, so a renamed one would be a
                    // tenant filter wearing the package's name while align restores the original.
                    // No helper text: it would push the field out of line with the others; the
                    // package chip's tooltip says why the fields are locked.
                    readOnly={isPackageFilter}
                    rules={{
                        validate: () => {
                            const drafts: ReadFilterDraft[] = getValues('readFilters');
                            return toRuleResult(validateCode(drafts, index));
                        },
                    }}
                />
                <TextFieldElement
                    control={control}
                    id={`readFilters.${index}.timeoutMs`}
                    name={`readFilters.${index}.timeoutMs`}
                    label={intl.formatMessage({
                        id: 'read-filters.timeout',
                        defaultMessage: 'Timeout (ms, empty = 5000)',
                    })}
                    rules={{ validate: (value: string) => toRuleResult(validateTimeout(value)) }}
                />
                {source && (
                    <InlineToggle>
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'read-filters.source-tooltip',
                                defaultMessage:
                                    'Shipped by the tenant package: its code, expression and description cannot be changed here, and align restores the shipped version when it is removed or changed.',
                            })}
                        >
                            <Chip
                                size="small"
                                label={intl.formatMessage(
                                    {
                                        id: 'read-filters.source',
                                        defaultMessage: 'Package {source}',
                                    },
                                    { source },
                                )}
                            />
                        </Tooltip>
                    </InlineToggle>
                )}
                <Box flex={1} />
                <InlineToggle>
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'read-filters.remove',
                            defaultMessage: 'Remove filter',
                        })}
                    >
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'read-filters.remove',
                                defaultMessage: 'Remove filter',
                            })}
                            onClick={onRemove}
                        >
                            <DeleteOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </InlineToggle>
            </Stack>
            <TextFieldElement
                required
                multiLine
                control={control}
                id={`readFilters.${index}.expression`}
                name={`readFilters.${index}.expression`}
                // The package owns its filter's rule: a change comes from a release, through align.
                readOnly={isPackageFilter}
                label={intl.formatMessage({
                    id: 'read-filters.expression',
                    defaultMessage: 'Expression (Cypher predicate on n)',
                })}
                rules={{ validate: (value: string) => toRuleResult(validateExpression(value)) }}
            />
            <TextFieldElement
                control={control}
                id={`readFilters.${index}.description`}
                name={`readFilters.${index}.description`}
                readOnly={isPackageFilter}
                label={intl.formatMessage({
                    id: 'read-filters.description',
                    defaultMessage: 'Description',
                })}
            />
            <Stack flexDirection="row" alignItems="center" gap={1}>
                <Button
                    variant="text"
                    startIcon={<PlayArrowIcon />}
                    disabled={!canExplain}
                    loading={loading}
                    onClick={() =>
                        explain(
                            expression.trim(),
                            timeout !== undefined && !Number.isNaN(timeout) ? timeout : undefined,
                        )
                    }
                >
                    {intl.formatMessage({
                        id: 'read-filters.explain',
                        defaultMessage: 'Explain',
                    })}
                </Button>
                {result && (
                    <Typography variant="body2">
                        {intl.formatMessage(
                            {
                                id: 'read-filters.explain-count',
                                defaultMessage:
                                    '{count, plural, one {# node of this schema passes} other {# nodes of this schema pass}} the filter for you',
                            },
                            { count: result.count },
                        )}
                    </Typography>
                )}
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {result && result.plan.length > 0 && (
                <Box
                    component="pre"
                    m={0}
                    p={1}
                    fontSize={12}
                    bgcolor="action.hover"
                    borderRadius={1}
                    data-testid="read-filter-plan"
                >
                    {result.plan
                        .map(line => `${'  '.repeat(line.depth)}${line.operator}`)
                        .join('\n')}
                </Box>
            )}
        </Stack>
    );
}
